import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import {
  acceptedText,
  applyAccepted,
  isSourced,
  proposalCounts,
  type ContentProposal,
  type ProposedField,
} from "@shared/content-proposal";
import { localeMeta } from "@shared/i18n";
import { foyerApi } from "../../http/foyer";
import EditorCard from "../template/EditorCard";
import { EDITOR_MAX_WIDTH } from "../template/editorLayout";
import { tokens } from "../../theme";
import { describeError, useToast } from "../toast";
import { Trans, useTranslation } from "react-i18next";
import type { Translate } from "../../i18n/translate";
import { useCommitNext } from "../template/EditSection";
import type { PremiseCommit } from "../../context/PremiseWorkspace";
import { PermissionGate } from "../../auth/access";

/** AI translation drafts and their review are Nenda setup work. */
const DRAFT_TRANSLATIONS = {
  resource: "tv-layout/translations",
  level: "administrate",
} as const;

/**
 * The review screen. Everything the model wrote passes through a person here.
 *
 * ## The two things this UI is responsible for
 *
 * 1. **Making an unsourced claim look different from a translation.** Every row
 *    shows the source text it came from, right there, so "is this right?" is
 *    answerable without opening another tab. A row with no source is tinted and
 *    labelled, and "Accept all" does not touch it - the button that gets pressed
 *    without reading can only ever accept text with a traceable origin.
 * 2. **Writing only what was reviewed.** Accepted lines are written to the
 *    premise by "Save translations", and by nothing else. The button says
 *    "Save", not "Publish": it stores the text on the premise, exactly as
 *    typing it would, and the TV picks it up the way it picks up any edit.
 */

interface Props {
  premise: PremiseConfig;
  /** Saves a change to the premise and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}

const POLL_MS = 2_000;

export default function TranslationReview({ premise, onCommit }: Props) {
  const toast = useToast();
  const { t } = useTranslation(["templates", "common"]);
  const commitNext = useCommitNext(premise, onCommit);
  const [availability, setAvailability] = useState<
    | { available: true; kind: string }
    | { available: false; reason: string }
    | null
  >(null);
  const [proposal, setProposal] = useState<ContentProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState<{
    count: number;
    skipped: string[];
  } | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    foyerApi
      .aiAvailability()
      .then(setAvailability)
      .catch(() => {
        setAvailability({
          available: false,
          reason: t("translationReview.unreachable"),
        });
      });
  }, [t]);

  // Load the most recent reviewable proposal for this premise, if any.
  useEffect(() => {
    if (!premise.premiseId) return;
    foyerApi
      .listProposals(premise.premiseId)
      .then(({ proposals }) => {
        const open = proposals.find(
          (p) => p.state === "ready" || p.state === "generating"
        );
        setProposal(open ?? null);
      })
      .catch(() => setProposal(null));
  }, [premise.premiseId]);

  // Poll while a run is in flight. The proposal row IS the progress indicator -
  // the request that started it returned immediately, so a slow model never
  // becomes a hanging fetch a proxy will cut at sixty seconds.
  useEffect(() => {
    if (proposal?.state !== "generating") {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    const id = proposal.id;
    pollRef.current = window.setInterval(() => {
      foyerApi
        .getProposal(id)
        .then(setProposal)
        .catch(() => {
          /* transient; the next tick tries again */
        });
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [proposal?.state, proposal?.id]);

  const counts = useMemo(
    () => proposalCounts(proposal?.fields ?? []),
    [proposal]
  );

  const start = useCallback(async () => {
    setBusy(true);
    setApplied(null);
    try {
      setProposal(await foyerApi.startTranslation(premise.premiseId));
    } catch (err) {
      toast.error(describeError(t, "startTranslationRun", err));
    } finally {
      setBusy(false);
    }
  }, [premise.premiseId, toast, t]);

  const decide = useCallback(
    async (
      field: ProposedField,
      decision: ProposedField["decision"],
      edited?: string
    ) => {
      if (!proposal) return;
      try {
        setProposal(
          await foyerApi.decideProposalField({
            id: proposal.id,
            fieldId: field.id,
            decision,
            edited,
          })
        );
      } catch (err) {
        toast.error(describeError(t, "recordDecision", err));
      }
    },
    [proposal, toast, t]
  );

  /**
   * Save the accepted fields to the premise.
   *
   * `applyAccepted` re-checks each target against the CONTENT AS IT IS NOW, so
   * anything the operator edited during the review is reported back rather than
   * overwritten. Those refusals are shown - a silent skip would look like the
   * button half-worked.
   */
  const saveTranslations = useCallback(async () => {
    if (!proposal || !premise.scheme) return;
    setBusy(true);
    try {
      const result = applyAccepted(
        premise.scheme.content,
        proposal.fields,
        proposal.defaultLocale
      );
      const n = result.applied.length;
      const saved = await commitNext(
        {
          ...premise,
          scheme: { ...premise.scheme, content: result.content },
        },
        {
          success: t("translationReview.savedToast", {
            count: n,
            name: premise.premiseName,
          }),
          failure: "saveTranslationsFor",
          failureParams: { name: premise.premiseName },
        }
      );
      // A failed save has already toasted; the review stays as it was so the
      // operator can try again without re-deciding every line.
      if (!saved) return;
      setProposal(
        await foyerApi.markProposalApplied(proposal.id, result.applied)
      );
      setApplied({
        count: n,
        skipped: result.skipped.map((s) =>
          s.reason === "changed_since_generated"
            ? t("translationReview.skippedChanged", {
                label: s.field.label,
                language: localeMeta(s.field.locale).label,
                actual: s.actual,
              })
            : t("translationReview.skippedGone", {
                label: s.field.label,
                language: localeMeta(s.field.locale).label,
              })
        ),
      });
    } catch (err) {
      toast.error(describeError(t, "recordTranslationsApplied", err));
    } finally {
      setBusy(false);
    }
  }, [proposal, premise, commitNext, toast, t]);

  if (availability && !availability.available) {
    return (
      <Box sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
        <Alert severity="info">
          <strong>{t("translationReview.notOn")}</strong> {availability.reason}
        </Alert>
      </Box>
    );
  }

  const blocked = whyBlocked(premise, t);

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <EditorCard
        eyebrow={t("translationReview.eyebrow")}
        action={
          <PermissionGate
            {...DRAFT_TRANSLATIONS}
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={busy || !!blocked || proposal?.state === "generating"}
              onClick={start}
            >
              {proposal?.state === "generating"
                ? t("translationReview.working")
                : t("translationReview.draft")}
            </Button>
          </PermissionGate>
        }
      >
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          <Trans
            t={t}
            i18nKey="translationReview.intro"
            values={{
              language: localeMeta(premise.defaultLocale || "en").label,
            }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        {blocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {blocked}
          </Alert>
        )}
        {availability?.available && availability.kind === "fake" && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Trans
              t={t}
              i18nKey="translationReview.fakeKey"
              components={{ code: <code /> }}
            />
          </Alert>
        )}
      </EditorCard>

      {/* The success itself is a toast; this stays only for lines that were
          NOT saved, because that is a fact the operator has to read. */}
      {applied && applied.skipped.length > 0 && (
        <Alert severity="warning">
          <strong>
            {t("translationReview.appliedSummary", {
              count: applied.count,
              skipped: applied.skipped.length,
            })}
          </strong>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {applied.skipped.map((s) => (
              <li key={s}>
                <Typography variant="caption">{s}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {proposal?.state === "generating" && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("translationReview.generating", {
              languages: proposal.locales
                .map((l) => localeMeta(l).label)
                .join(", "),
            })}
          </Typography>
        </Stack>
      )}

      {proposal?.state === "failed" && (
        <Alert severity="error">
          <strong>{t("translationReview.failedTitle")}</strong> {proposal.error}{" "}
          {t("translationReview.nothingChanged")}
        </Alert>
      )}

      {proposal?.state === "ready" && (
        <ReviewList
          proposal={proposal}
          counts={counts}
          busy={busy}
          onDecide={decide}
          onAcceptSourced={async () => {
            try {
              setProposal(await foyerApi.acceptSourcedFields(proposal.id));
            } catch (err) {
              toast.error(describeError(t, "acceptSourcedLines", err));
            }
          }}
          onSaveTranslations={saveTranslations}
          onDiscard={async () => {
            try {
              await foyerApi.discardProposal(proposal.id);
              setProposal(null);
            } catch (err) {
              toast.error(describeError(t, "discardDraftTranslations", err));
            }
          }}
        />
      )}
    </Stack>
  );
}

/** Why the button is disabled, in words the operator can act on. */
function whyBlocked(premise: PremiseConfig, t: Translate): string | null {
  if (!premise.scheme) {
    return t("templates:translationReview.noTemplate");
  }
  if (
    (premise.supportedLocales ?? []).filter((l) => l !== premise.defaultLocale)
      .length === 0
  ) {
    return t("templates:translationReview.oneLanguage");
  }
  return null;
}

function ReviewList({
  proposal,
  counts,
  busy,
  onDecide,
  onAcceptSourced,
  onSaveTranslations,
  onDiscard,
}: {
  proposal: ContentProposal;
  counts: ReturnType<typeof proposalCounts>;
  busy: boolean;
  onDecide: (
    f: ProposedField,
    d: ProposedField["decision"],
    edited?: string
  ) => void;
  onAcceptSourced: () => void;
  onSaveTranslations: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const byLocale = useMemo(() => {
    const map = new Map<string, ProposedField[]>();
    for (const f of proposal.fields) {
      const list = map.get(f.locale) ?? [];
      list.push(f);
      map.set(f.locale, list);
    }
    return [...map.entries()];
  }, [proposal.fields]);

  return (
    <>
      <EditorCard
        eyebrow={t("translationReview.reviewEyebrow")}
        action={
          <Stack direction="row" spacing={1}>
            <PermissionGate
              {...DRAFT_TRANSLATIONS}
              deniedProps={{ disabled: true }}
            >
              <Button size="small" onClick={onDiscard} disabled={busy}>
                {t("translationReview.discard")}
              </Button>
            </PermissionGate>
            <Tooltip
              title={
                counts.unsourced > 0
                  ? t("translationReview.unsourced", {
                      count: counts.unsourced,
                    })
                  : ""
              }
            >
              <span>
                <PermissionGate
                  {...DRAFT_TRANSLATIONS}
                  deniedProps={{ disabled: true }}
                >
                  <Button
                    size="small"
                    onClick={onAcceptSourced}
                    disabled={busy || counts.pending === counts.unsourced}
                  >
                    {t("translationReview.acceptAll")}
                  </Button>
                </PermissionGate>
              </span>
            </Tooltip>
            <PermissionGate
              {...DRAFT_TRANSLATIONS}
              deniedProps={{ disabled: true }}
            >
              <Button
                size="small"
                variant="contained"
                onClick={onSaveTranslations}
                disabled={busy || counts.accepted === 0}
              >
                {t("translationReview.saveCount", { count: counts.accepted })}
              </Button>
            </PermissionGate>
          </Stack>
        }
      >
        {/* A review list nobody asked for has to explain itself. Without this
            line, an operator who added a language yesterday opens Languages and
            finds forty rows of German waiting with no account of where they
            came from - which reads as the system having done something on its
            own, not as a convenience. */}
        {autoReason(proposal) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {autoReason(proposal)} {t("translationReview.autoNothingChanged")}
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={t("translationReview.drafted", { count: counts.total })}
          />
          <Chip
            size="small"
            color="success"
            label={t("translationReview.acceptedCount", {
              count: counts.accepted,
            })}
          />
          <Chip
            size="small"
            label={t("translationReview.toReview", { count: counts.pending })}
          />
          {counts.rejected > 0 && (
            <Chip
              size="small"
              label={t("translationReview.rejectedCount", {
                count: counts.rejected,
              })}
            />
          )}
          {counts.edited > 0 && (
            <Chip
              size="small"
              color="info"
              label={t("translationReview.reworded", { count: counts.edited })}
            />
          )}
        </Stack>

        {proposal.notes.length > 0 && (
          <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.5 }}>
            {proposal.notes.map((n) => (
              <li key={n.code}>
                <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                  {n.detail}
                </Typography>
              </li>
            ))}
          </Box>
        )}

        {proposal.generator && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 2, color: tokens.inkFaint }}
          >
            {t("translationReview.draftedBy", {
              provider: proposal.generator.provider,
              model: proposal.generator.model,
              version: proposal.generator.promptVersion,
            })}
          </Typography>
        )}
      </EditorCard>

      {byLocale.map(([locale, fields]) => (
        <EditorCard
          key={locale}
          eyebrow={localeMeta(locale).label}
          action={
            <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
              {t("translationReview.localeAccepted", {
                accepted: fields.filter((f) => f.decision === "accepted")
                  .length,
                total: fields.length,
              })}
            </Typography>
          }
        >
          <Stack divider={<Divider flexItem />} spacing={0}>
            {fields.map((field) => (
              <FieldRow key={field.id} field={field} onDecide={onDecide} />
            ))}
          </Stack>
        </EditorCard>
      ))}
    </>
  );
}

function FieldRow({
  field,
  onDecide,
}: {
  field: ProposedField;
  onDecide: (
    f: ProposedField,
    d: ProposedField["decision"],
    edited?: string
  ) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [draft, setDraft] = useState(acceptedText(field));
  const sourced = isSourced(field);

  // Re-seed the box when the row is replaced by a server response. `field` is a
  // fresh object on every fetch, so depending on the whole thing is both what
  // the linter wants and what is correct: a decision round-trip must put the
  // canonical text back, or a reviewer's edit and the stored value drift apart.
  useEffect(() => setDraft(acceptedText(field)), [field]);

  return (
    <Box
      sx={{
        py: 1.75,
        // An unsourced row is tinted for the whole width. It is the one visual
        // difference that has to survive a tired operator scrolling quickly.
        ...(sourced ? {} : { bgcolor: tokens.rowAlt, px: 1.5, mx: -1.5 }),
        opacity: field.decision === "rejected" ? 0.5 : 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.75 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          variant="caption"
          sx={{ typography: "eyebrow", color: tokens.inkFaint }}
        >
          {field.label}
        </Typography>
        {!sourced && (
          <Chip
            size="small"
            color="warning"
            label={t("translationReview.noSource")}
          />
        )}
        {field.decision === "accepted" && (
          <Chip
            size="small"
            color="success"
            label={t("translationReview.accepted")}
          />
        )}
      </Stack>

      {field.evidence && (
        <Typography
          variant="body2"
          sx={{ color: tokens.inkMuted, mb: 1, fontStyle: "italic" }}
        >
          {t("translationReview.quoted", { quote: field.evidence.quote })}
        </Typography>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems="flex-start"
      >
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft !== acceptedText(field)) {
              onDecide(field, "accepted", draft);
            }
          }}
        />
        {/* Explicit basis on both sides: a flex row with one unbounded prose
            column collapses its sibling - the trap the card-language note
            records from the header and the device dialog. */}
        <Stack direction="row" spacing={1} sx={{ flex: "0 0 auto" }}>
          <PermissionGate
            {...DRAFT_TRANSLATIONS}
            deniedProps={{ disabled: true }}
          >
            <Button
              size="small"
              variant={field.decision === "accepted" ? "contained" : "outlined"}
              onClick={() => onDecide(field, "accepted", draft)}
            >
              {t("translationReview.accept")}
            </Button>
          </PermissionGate>
          <PermissionGate
            {...DRAFT_TRANSLATIONS}
            deniedProps={{ disabled: true }}
          >
            <Button
              size="small"
              color="inherit"
              variant={field.decision === "rejected" ? "contained" : "outlined"}
              onClick={() => onDecide(field, "rejected")}
            >
              {t("translationReview.reject")}
            </Button>
          </PermissionGate>
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * The sentence a self-started run left on its first history entry, if it was
 * self-started. A run somebody pressed the button for carries none, and says
 * nothing extra.
 */
function autoReason(proposal: ContentProposal): string | null {
  return proposal.history?.[0]?.note ?? null;
}
